/**
 * AppLayout — Two-column sidebar + enhanced TopBar + MDI tabs + content
 *
 * **100% DSL-driven**: TopBar component visibility controlled by
 * LayoutConfig.features (search, notifications, accent, theme, etc.)
 *
 * ┌─────────────────────────────────────────────────┐
 * │ ┌────┐┌──────────┐┌─────────────────────────────┐│
 * │ │Icon││  Submenu  ││ TopBar (search/accent/…)    ││
 * │ │Rail││          │├─────────────────────────────┤│
 * │ │w-14││ w-[200px] ││ MDI Tabs                    ││
 * │ │    ││          │├─────────────────────────────┤│
 * │ │    ││          ││ Content (Outlet)             ││
 * │ └────┘└──────────┘└─────────────────────────────┘│
 * └─────────────────────────────────────────────────┘
 *
 * Supports ?nav=false for standalone / embedded mode.
 */

import { useEffect, useCallback, useState } from 'react';
import { Outlet, useSearchParams, useNavigate, useLocation, matchPath } from 'react-router-dom';
import { TabKeepAlive } from '@/components/TabKeepAlive';
import { Sidebar } from './Sidebar';
import { useAuth } from '@/lib/auth';
import { isChatOnlyAccess } from '@/lib/chat-only-access';
import { useAppStore } from '@/stores/app-store';
import { useMdiStore, type MdiTab } from '@/stores/mdi-store';
import { useLayoutConfigContext, type NavigationCategory, type NavigationItem } from '@/lib/use-layout-config';
import { cn } from '@/lib/utils';
import {
  fmRouteForLegacyFmsPath,
} from '@/lib/dynamic-runtime-bindings';
import { isPathAllowedByStrictNavigation } from '@/lib/strict-navigation';
import {
  isPathInResolvedDynamicRuntimeBinding,
  resolvedDynamicRuntimeBindingForPath,
  titleForResolvedRuntimePath,
} from '@/lib/runtime-api';
import { MDITabs } from '@/components/MDITabs';
import { CommandPalette } from '@/components/CommandPalette';
import { UserDropdown } from '@/components/UserDropdown';
import { AccentSwitch } from '@/components/AccentSwitch';
import { NotificationBell } from '@/components/NotificationBell';
import { RealtimeStatusAggregator } from '@/components/RealtimeStatusAggregator';
import { ThemeLanguageToggle } from '@/components/ThemeLanguageToggle';
import { Search, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ChatSlidePanel } from '@/components/ChatSlidePanel';
import { TenantSwitchPanel } from '@/components/TenantSwitchPanel';
import { usePageContext } from '@/hooks/usePageContext';
import { MomentumBar, useMomentum, type MomentumSignal } from '@boox/ui-foundation';
import { useMomentumPageStore } from '@/stores/momentum-page-store';
import { trackPageView } from '@/lib/analytics';
import { sanitizeUserDisplay, sanitizeUserEmail } from '@/lib/user-display';

const AIW_PIPELINE_TAB_TITLES: Record<string, string> = {
  inbox: 'Inbox',
  'intake-message-triage': 'Inbox',
  'intake-ocr': 'Documents & Photos',
  'intake-job-draft': 'Job Drafts',
  'intake-follow-up': 'Missing Info',
  'billing-proof-check': 'Ready to Bill',
  'billing-rate-validation': 'Price Check',
  'billing-invoice-draft': 'Draft Invoices',
  'billing-soa-follow-up': 'Payment Follow-up',
  'audit-compliance': 'Process Check',
  'audit-exception': 'Problem Cases',
  'audit-attachment-gap': 'Missing Documents',
  'audit-charge-anomaly': 'Charge Issues',
  'ceo-daily-brief': 'Today Summary',
  'ceo-margin-watch': 'Profit Watch',
  'ceo-bottleneck': 'Delay Watch',
  'ceo-customer-risk': 'Customer Risk',
};

function titleForAiwPath(path: string): string {
  const query = path.includes('?') ? path.slice(path.indexOf('?') + 1) : '';
  const params = new URLSearchParams(query);
  const tab = params.get('tab');
  if (tab === 'team') return 'AI Activity';
  if (tab === 'deploy') return 'AI Setup';

  const pipeline = params.get('pipeline');
  if (pipeline && AIW_PIPELINE_TAB_TITLES[pipeline]) return AIW_PIPELINE_TAB_TITLES[pipeline];
  if (params.get('copilot') === 'intake') return 'Inbox';
  if (params.get('copilot') === 'billing') return 'Billing AI';
  if (params.get('copilot') === 'audit') return 'Audit AI';
  if (params.get('copilot') === 'ceo') return 'Management AI';
  return 'AI Overview';
}

function pathnameForPath(path: string): string {
  return path.split('?')[0] ?? path;
}

function sdkCanonicalPathForLegacyModule(path: string): string | null {
  const pathname = pathnameForPath(path);
  const suffix = path.includes('?') ? path.slice(path.indexOf('?')) : '';
  if (pathname === '/tms' || pathname.startsWith('/tms/')) {
    return `${pathname.replace(/^\/tms\b/, '/tm')}${suffix}`;
  }
  if (pathname === '/wms' || pathname.startsWith('/wms/')) {
    return `${pathname.replace(/^\/wms\b/, '/wm')}${suffix}`;
  }
  if (pathname === '/hms' || pathname.startsWith('/hms/')) {
    return `${pathname.replace(/^\/hms\b/, '/ct')}${suffix}`;
  }
  if (pathname === '/fms' || pathname.startsWith('/fms/')) {
    return fmRouteForLegacyFmsPath(path);
  }
  if (pathname === '/scm' || pathname.startsWith('/scm/')) {
    return `${pathname.replace(/^\/scm\b/, '/sc')}${suffix}`;
  }
  if (pathname === '/acc' || pathname.startsWith('/acc/')) {
    return `${pathname.replace(/^\/acc\b/, '/ac')}${suffix}`;
  }
  if (pathname === '/aiw' || pathname.startsWith('/aiw/')) {
    return `${pathname.replace(/^\/aiw\b/, '/ai')}${suffix}`;
  }
  if (pathname === '/aic' || pathname.startsWith('/aic/')) {
    return `${pathname.replace(/^\/aic\b/, '/ai')}${suffix}`;
  }
  if (pathname === '/maritime-desk' || pathname.startsWith('/maritime-desk/')) {
    return `${pathname.replace(/^\/maritime-desk\b/, '/mt')}${suffix}`;
  }
  if (pathname === '/shipping-agency' || pathname.startsWith('/shipping-agency/')) {
    return `${pathname.replace(/^\/shipping-agency\b/, '/mt')}${suffix}`;
  }
  if (pathname === '/liner' || pathname.startsWith('/liner/')) {
    return `${pathname.replace(/^\/liner\b/, '/mt')}${suffix}`;
  }
  return null;
}

function defaultPathForStrictNavigation(navigation: NavigationCategory[]): string {
  const firstCategory = navigation[0];
  return firstCategory?.dynamicRuntime?.defaultRoute
    ?? firstCategory?.items[0]?.route
    ?? '/';
}

function defaultTitleForStrictNavigation(navigation: NavigationCategory[], fallback: string): string {
  const firstCategory = navigation[0];
  return firstCategory?.label ?? firstCategory?.items[0]?.labelPlural ?? fallback;
}

function isWascoTenant(tenantSlug?: string | null): boolean {
  return String(tenantSlug ?? '').trim().toLowerCase() === 'wasco-malaysia';
}

function isNewRevoTenant(tenantSlug?: string | null): boolean {
  return String(tenantSlug ?? '').trim().toLowerCase() === 'new-revo';
}

function navigationItemForPath(pathname: string, navigation: NavigationCategory[]): NavigationItem | null {
  let best: NavigationItem | null = null;
  for (const item of navigation.flatMap((category) => category.items)) {
    const route = pathnameForPath(item.route);
    if (pathname !== route && !pathname.startsWith(`${route}/`)) continue;
    if (!best || route.length > pathnameForPath(best.route).length) best = item;
  }
  return best;
}

function labelForNavigationItem(
  t: ReturnType<typeof useTranslation>['t'],
  item: NavigationItem | null,
): string | null {
  if (!item) return null;
  if (item.labelPluralKey) return t(item.labelPluralKey, item.labelPlural);
  if (item.labelKey) return t(item.labelKey, item.labelPlural || item.label);
  return item.labelPlural || item.label || null;
}

export function AppLayout() {
  const { user, logout, tenants, switchTenant, switchGroupTenant } = useAuth();
  const isWasco = isWascoTenant(user?.tenantSlug);
  const isNewRevo = isNewRevoTenant(user?.tenantSlug);
  const { sidebarCollapsed, setChatPanelOpen, setActiveCategory } = useAppStore();
  const { tabs, activeTabId, openTab, ensureTab, closeTab, closeAllTabs, closeTabsToDashboard, activateTab, updateTabTitle, sessionScope } = useMdiStore();
  const { config } = useLayoutConfigContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [cmdOpen, setCmdOpen] = useState(false);
  const chatOnlyAccess = isChatOnlyAccess(user);
  const [tenantSwitching, setTenantSwitching] = useState(false);

  // Resolve current tenant name for header display
  const currentTenant = tenants?.find((tn) => tn.id === user?.tenantId);
  const tenantName = currentTenant?.name ?? config.branding?.appName ?? 'Boox';
  const isCcAdminShell = user?.role === 'platform_admin'
    || (typeof window !== 'undefined' && window.location.port === '3002');
  const searchPlaceholder = isCcAdminShell ? '搜索菜单、页面、数据' : t('search.placeholder', '搜索菜单、页面、数据');

  const handleTenantSwitch = useCallback(async (tenantId: string, groupId?: string) => {
    if (!tenantId || tenantId === user?.tenantId) return;
    setTenantSwitching(true);
    let switched = false;
    try {
      if (groupId) await switchGroupTenant(groupId, tenantId);
      else await switchTenant(tenantId);
      switched = true;
      setActiveCategory('home');
      window.location.replace('/');
    } finally {
      if (!switched) setTenantSwitching(false);
    }
  }, [setActiveCategory, user?.tenantId, switchGroupTenant, switchTenant]);

  // Keep chat context store in sync with current route
  usePageContext();

  // ── Pageview tracking (fire-and-forget, Log DB) ──
  useEffect(() => {
    trackPageView(location.pathname, {
      userId: user?.id,
      userDisplayName: user?.name,
      tenantId: user?.tenantId,
    });
  }, [location.pathname, user?.id, user?.name, user?.tenantId]);

  // ── Momentum signals from current page ──
  const momentumPageData = useMomentumPageStore((s) => s.data);
  const momentum = useMomentum({
    pageKey: momentumPageData?.pageKey ?? location.pathname,
    entity: momentumPageData?.entity,
    entities: momentumPageData?.entities,
    config: momentumPageData?.config,
  });

  // ── Momentum action handler: navigate to route or open AI chat ──
  const handleMomentumAction = useCallback((signal: MomentumSignal) => {
    const action = signal.suggestedAction;
    if (!action) return;
    if (action.route) {
      navigate(action.route);
    } else if (action.prompt) {
      setChatPanelOpen(true);
    }
  }, [navigate, setChatPanelOpen]);

  useEffect(() => {
    const handler = () => setChatPanelOpen(true);
    window.addEventListener('boox:open-chat', handler);
    return () => window.removeEventListener('boox:open-chat', handler);
  }, [setChatPanelOpen]);

  const showNav = searchParams.get('nav') !== 'false';
  const currentRoutePath = `${location.pathname}${location.search || ''}`;
  const hasHomeTab = tabs.some((tab) => (tab.path.split('?')[0] ?? tab.path) === '/');
  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? null;
  const activeTabPath = activeTab?.path ?? null;
  const hasTabForCurrentRoute = tabs.some((tab) => tab.path === currentRoutePath);
  const currentDynamicRuntimeBinding = resolvedDynamicRuntimeBindingForPath(location.pathname);
  const isDynamicRuntimeWorkspace = Boolean(currentDynamicRuntimeBinding);
  const strictNavigationEnabled = Boolean((config as { strictNavigation?: boolean }).strictNavigation);
  const strictNavigationDefaultPath = defaultPathForStrictNavigation(config.navigation);
  const strictNavigationDefaultTitle = defaultTitleForStrictNavigation(config.navigation, t('dashboard.title'));
  const strictNavigationDefaultIcon = config.navigation[0]?.icon ?? 'layout-dashboard';
  const isCurrentDynamicRuntimeTab = useCallback((tab: MdiTab) => {
    if (!currentDynamicRuntimeBinding) return false;
    const tabPathname = tab.path.split('?')[0] ?? tab.path;
    return isPathInResolvedDynamicRuntimeBinding(tabPathname, currentDynamicRuntimeBinding);
  }, [currentDynamicRuntimeBinding]);
  const isPinnedMdiTab = useCallback((tab: MdiTab) => !tab.closable || tab.path === '/', []);
  const visibleMdiTabs = [
    ...tabs.filter(isPinnedMdiTab),
    ...tabs.filter((tab) => (
      !isPinnedMdiTab(tab)
      && (!isDynamicRuntimeWorkspace || isCurrentDynamicRuntimeTab(tab))
    )),
  ];
  const keepAliveTabs = isDynamicRuntimeWorkspace ? visibleMdiTabs : tabs;
  const hasRenderableActiveTab = !isDynamicRuntimeWorkspace || visibleMdiTabs.some((tab) => tab.id === activeTabId);
  const shouldRenderKeepAlive = Boolean(
    activeTabId
    && hasRenderableActiveTab
    && hasTabForCurrentRoute
    && activeTabPath === currentRoutePath,
  );

  // Feature flags from DSL config
  const features = config.features;

  // ── ⌘K keyboard shortcut (only if command palette enabled) ──
  useEffect(() => {
    if (chatOnlyAccess || !features.commandPaletteEnabled) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [chatOnlyAccess, features.commandPaletteEnabled]);

  // ── Sync route changes → MDI tab (auto-open tab on navigation) ──
  // Covers ALL routes so keep-alive works for admin pages too.
  useEffect(() => {
    const pathname = location.pathname;
    const path = `${location.pathname}${location.search || ''}`;
    if (pathname === '/login') return;
    const sdkCanonicalPath = sdkCanonicalPathForLegacyModule(path);
    if (sdkCanonicalPath) {
      navigate(sdkCanonicalPath, { replace: true });
      return;
    }
    if (strictNavigationEnabled && config.navigation.length > 0) {
      ensureTab({
        title: strictNavigationDefaultTitle,
        path: strictNavigationDefaultPath,
        icon: strictNavigationDefaultIcon,
        closable: false,
      });
      if (!isPathAllowedByStrictNavigation(pathname, config.navigation)) {
        navigate(strictNavigationDefaultPath, { replace: true });
        return;
      }
    }
    const navItemForPath = navigationItemForPath(pathname, config.navigation);
    const navTitleForPath = labelForNavigationItem(t, navItemForPath);

    if (isWasco && (
      pathname === '/ai'
      || pathname.startsWith('/ai/')
      || pathname === '/aiw'
      || pathname.startsWith('/aiw/')
      || pathname === '/aic'
      || pathname.startsWith('/aic/')
      || pathname === '/chat'
      || pathname.startsWith('/chat/')
      || pathname === '/builder'
      || pathname.startsWith('/builder/')
      || pathname === '/crm'
      || pathname.startsWith('/crm/')
      || pathname === '/rm'
      || pathname.startsWith('/rm/')
      || pathname === '/today'
    )) {
      navigate('/wp', { replace: true });
      return;
    }

    if (pathname === '/') {
      if (isWasco) {
        navigate('/wp', { replace: true });
        return;
      }
      if (strictNavigationEnabled && strictNavigationDefaultPath !== '/') {
        navigate(strictNavigationDefaultPath, { replace: true });
        return;
      }
      openTab({ title: t('dashboard.title'), path: '/', icon: 'dashboard', closable: false });
      return;
    }

    if (!isWasco && !chatOnlyAccess && !strictNavigationEnabled && !hasHomeTab) {
      ensureTab({ title: t('dashboard.title'), path: '/', icon: 'dashboard', closable: false });
    }

    // /e/:slug/:id  or  /e/:slug
    const entityDetail = matchPath('/e/:slug/:id', pathname);
    if (entityDetail?.params) {
      const { slug } = entityDetail.params;
      openTab({ title: slug ?? 'Entity', path, icon: 'file-text' });
      return;
    }
    const entityList = matchPath('/e/:slug', pathname);
    if (entityList?.params.slug) {
      openTab({ title: entityList.params.slug, path, icon: 'briefcase' });
      return;
    }

    // /chat  or  /chat/:convId
    const chatDetail = matchPath('/chat/:convId', pathname);
    if (chatDetail) {
      openTab({ title: 'Chat', path, icon: 'bot' });
      return;
    }
    if (pathname === '/chat') {
      openTab({ title: 'Chat', path, icon: 'bot' });
      return;
    }

    if (pathname === '/aiw' || pathname === '/aiw/inbox') {
      openTab({ title: titleForAiwPath(path), path, icon: 'bot', closable: true });
      return;
    }

    // Inbox routes
    const inboxTitles: Record<string, string> = {
      conversations: 'Conversations',
      'email-messages': 'Email Messages',
      messaging: 'Messaging',
      outbox: 'Outbox',
      'intake-review': 'AI Review',
    };
    const inboxMatch = matchPath('/inbox/:page', pathname);
    if (inboxMatch?.params.page) {
      const page = inboxMatch.params.page;
      openTab({ title: inboxTitles[page] ?? `Inbox ${page}`, path, icon: 'inbox', closable: true });
      return;
    }

    // ── Network routes ──
    const networkPageTitles: Record<string, string> = {
      directory: 'Network Directory',
      connections: 'Connections',
      alliances: 'Alliances',
      messages: 'Messages',
      channels: 'Channels',
      circles: 'Circles',
      group: 'Group',
      rfq: 'RFQ',
    };
    const networkNestedMatch = matchPath('/network/:page/:id', pathname);
    if (networkNestedMatch?.params.page) {
      const page = networkNestedMatch.params.page;
      openTab({ title: networkPageTitles[page] ?? `Network ${page}`, path, icon: 'network', closable: true });
      return;
    }
    const networkMatch = matchPath('/network/:page', pathname);
    if (networkMatch?.params.page) {
      const page = networkMatch.params.page;
      openTab({ title: networkPageTitles[page] ?? `Network ${page}`, path, icon: 'network', closable: true });
      return;
    }

    // ── Business Builder owner routes ──
    const builderPageTitles: Record<string, string> = {
      'business-home': 'Business Home',
      organization: 'Organization',
      'business-tree': 'Organization',
      artifacts: 'Artifacts',
      'world-view': 'World View',
      'business-map': 'World View',
      'ai-cos-workbench': 'AI CoS Workbench',
      'review-queue': 'Review Queue',
    };
    const builderMatch = matchPath('/builder/:page', pathname);
    if (builderMatch?.params.page) {
      const page = builderMatch.params.page;
      openTab({ title: builderPageTitles[page] ?? `Business Builder ${page}`, path, icon: 'map', closable: true });
      return;
    }

    // Admin routes
    const adminMatch = matchPath('/admin/:page', pathname);
    if (adminMatch?.params.page) {
      if (navTitleForPath) {
        openTab({ title: navTitleForPath, path, icon: navItemForPath?.icon ?? 'shield-check', closable: true });
        return;
      }
      const adminTitles: Record<string, string> = {
        'command-center': '指挥中心', 'team': 'AI 团队', 'logs': '日志',
        'alerts': '告警规则', 'bridges': '桥接', 'skills': '技能', 'policies': '策略', 'skill-config': '技能配置',
        'tools': '工具', 'prompts': '提示词', 'training': '训练',
        'memory': '记忆', 'templates': '模板', 'config-versions': '配置版本', 'surface-access': '入口权限',
        'schema': 'Schema 配置', 'workflows': '工作流', 'scripts': '脚本', 'recurring-tasks': '周期任务',
        'legacy-connectors': '旧连接器', 'data-transfer': '数据迁移',
      };
      const page = adminMatch.params.page;
      openTab({ title: adminTitles[page] ?? page, path, icon: 'shield-check', closable: true });
      return;
    }

    // ── Dynamic runtime module routes ──
    const dynamicRuntimeBinding = resolvedDynamicRuntimeBindingForPath(pathname);
    if (dynamicRuntimeBinding) {
      const dynamicRuntimeTitle = titleForResolvedRuntimePath(pathname, dynamicRuntimeBinding);
      const dynamicRuntimeTabTitle = dynamicRuntimeBinding.module === 'fm'
        ? dynamicRuntimeTitle
        : pathname === dynamicRuntimeBinding.defaultRoute
          ? dynamicRuntimeTitle
          : navItemForPath?.labelPlural ?? navItemForPath?.label ?? dynamicRuntimeTitle;
      openTab({
        title: dynamicRuntimeTabTitle,
        path,
        icon: navItemForPath?.icon ?? 'workflow',
        closable: true,
      });
      return;
    }

    // ── TMS legacy routes ──
    const tmsPageTitles: Record<string, string> = {
      jobs: 'Transport V1 Jobs', planner: 'Transport Hero V1 Planner', 'fcl-planner': 'FCL Planner', 'lcl-planner': 'LCL Planner', dispatch: 'Transport V1 Dispatch', outbox: 'Transport Outbox', billing: 'Transport V1 Billing', 'master-data': 'Transport V1 Master Data',
      documents: 'Transport V1 Documents', pricing: 'Transport V1 Pricing', setup: 'Transport V1 Setup',
    };
    if (pathname === '/tms') {
      openTab({ title: 'TMS Overview', path, icon: 'truck', closable: true });
      return;
    }
    const tmsJobDetail = matchPath('/tms/jobs/:jobId', pathname);
    if (tmsJobDetail?.params.jobId) {
      openTab({ title: 'Job Detail', path, icon: 'truck', closable: true });
      return;
    }
    const tmsMatch = matchPath('/tms/:page', pathname);
    if (tmsMatch?.params.page) {
      const page = tmsMatch.params.page;
      openTab({ title: tmsPageTitles[page] ?? `Transport V1 ${page}`, path, icon: 'truck', closable: true });
      return;
    }

    // ── WP routes (Wasco Project Warehouse) ──
    const wpPageTitles: Record<string, string> = {
      inbound: 'Receive From PO',
      inventory: 'WP Inventory',
      outbound: 'WP Outbound',
      'work-orders': 'Picking',
      'master-data': 'Master Data',
      'warehouse-setup': 'Warehouse Setup',
      documents: 'Documents',
      'stock-ledger': 'Stock Ledger',
      inspection: 'QC',
      'purchase-orders': 'PO Upload',
      'material-requests': 'Material Requests',
      'issue-orders': 'Outbound Issue',
      'stock-take': 'Stock Count',
      reports: 'Inventory Report',
      labels: 'Product Stickers',
      tasks: 'WP Tasks',
      setup: 'WP Setup',
      returns: 'Return',
    };
    if (pathname === '/wp') {
      openTab({ title: 'WP Overview', path, icon: 'package', closable: true });
      return;
    }
    const wpInboundEntry = matchPath('/wp/inbound/new', pathname);
    if (wpInboundEntry) {
      const isReturnMode = new URLSearchParams(path.split('?')[1] ?? '').get('mode') === 'RETURN';
      openTab({
        title: isReturnMode ? 'Material Return' : 'Receive From PO',
        path: isReturnMode ? '/wp/returns/form' : path,
        icon: 'package',
        closable: true,
      });
      return;
    }
    const wpReturnForm = matchPath('/wp/returns/form', pathname);
    if (wpReturnForm) {
      openTab({ title: 'Material Return', path, icon: 'package', closable: true });
      return;
    }
    const wpReturnGrn = matchPath('/wp/returns/grn', pathname);
    if (wpReturnGrn) {
      openTab({ title: 'Inventory Return', path, icon: 'package', closable: true });
      return;
    }
    const wpReturnProject = matchPath('/wp/returns/project', pathname);
    if (wpReturnProject) {
      openTab({ title: 'Inventory Transfer', path, icon: 'package', closable: true });
      return;
    }
    const wpPurchaseOrderDetail = matchPath('/wp/purchase-orders/:id', pathname);
    if (wpPurchaseOrderDetail?.params.id) {
      openTab({ title: 'Purchase Order', path, icon: 'package', closable: true });
      return;
    }
    const wpIssueOrderDetail = matchPath('/wp/issue-orders/:id', pathname);
    if (wpIssueOrderDetail?.params.id) {
      openTab({ title: 'Issue Order', path, icon: 'package', closable: true });
      return;
    }
    const wpMaterialRequestNew = matchPath('/wp/material-requests/new', pathname);
    if (wpMaterialRequestNew) {
      openTab({ title: 'New Material Request', path, icon: 'package', closable: true });
      return;
    }
    const wpMaterialRequestDetail = matchPath('/wp/material-requests/:id', pathname);
    if (wpMaterialRequestDetail?.params.id) {
      openTab({ title: 'Material Request', path, icon: 'package', closable: true });
      return;
    }
    const wpMatch = matchPath('/wp/:page', pathname);
    if (wpMatch?.params.page) {
      const page = wpMatch.params.page;
      openTab({ title: navTitleForPath ?? wpPageTitles[page] ?? `WP ${page}`, path, icon: navItemForPath?.icon ?? 'package', closable: true });
      return;
    }

    // ── WM routes ──
    const wmsPageTitles: Record<string, string> = {
      inbound: 'WM Inbound', inventory: 'WM Inventory', outbound: 'WM Outbound',
      'work-orders': 'WM Work Orders', planner: 'WM Planner', 'master-data': 'WM Master Data',
      schedule: 'WM Schedule Board',
      documents: 'WM Shared Documents', 'stock-ledger': 'WM Stock Movements', inspection: 'WM QC / Inspection',
      orders: 'WM Business Orders', tasks: 'WM Tasks', arrivals: 'WM Arrival Dock',
      'purchase-orders': 'WM PO', 'material-requests': 'WM Material Request', 'issue-orders': 'WM Issue Orders',
      setup: 'WM Setup',
    };
    const canonicalWmPath = path.startsWith('/wms') ? path.replace(/^\/wms/, '/wm') : path;
    const canonicalWmPathname = canonicalWmPath.split('?')[0] ?? canonicalWmPath;
    if (canonicalWmPathname === '/wm') {
      openTab({ title: 'WM Overview', path: canonicalWmPath, icon: 'package', closable: true });
      return;
    }
    const wmsInboundEntry = matchPath('/wm/inbound/new', canonicalWmPathname);
    if (wmsInboundEntry) {
      openTab({ title: 'Receive From PO', path: canonicalWmPath, icon: 'package', closable: true });
      return;
    }
    const wmsPurchaseOrderDetail = matchPath('/wm/purchase-orders/:id', canonicalWmPathname);
    if (wmsPurchaseOrderDetail?.params.id) {
      openTab({ title: 'Purchase Order', path: canonicalWmPath, icon: 'package', closable: true });
      return;
    }
    const wmsIssueOrderDetail = matchPath('/wm/issue-orders/:id', canonicalWmPathname);
    if (wmsIssueOrderDetail?.params.id) {
      openTab({ title: 'Issue Order', path: canonicalWmPath, icon: 'package', closable: true });
      return;
    }
      const wmsMaterialRequestNew = matchPath('/wm/material-requests/new', canonicalWmPathname);
      if (wmsMaterialRequestNew) {
        openTab({ title: 'New Material Request', path: canonicalWmPath, icon: 'package', closable: true });
        return;
      }
      const wmsMaterialRequestDetail = matchPath('/wm/material-requests/:id', canonicalWmPathname);
      if (wmsMaterialRequestDetail?.params.id) {
        openTab({ title: 'Material Request', path: canonicalWmPath, icon: 'package', closable: true });
        return;
      }
    const wmsMatch = matchPath('/wm/:page', canonicalWmPathname);
    if (wmsMatch?.params.page) {
      const page = wmsMatch.params.page;
      openTab({ title: wmsPageTitles[page] ?? `WM ${page}`, path: canonicalWmPath, icon: 'package', closable: true });
      return;
    }

    // ── Legacy FMS routes redirect to new FM runtime tabs ──
    if (pathname === '/fms' || pathname.startsWith('/fms/')) {
      const canonicalPath = fmRouteForLegacyFmsPath(path);
      const canonicalPathname = canonicalPath.split('?')[0] ?? canonicalPath;
      const binding = resolvedDynamicRuntimeBindingForPath(canonicalPathname);
      openTab({
        title: binding ? titleForResolvedRuntimePath(canonicalPathname, binding) : 'FM Overview',
        path: canonicalPath,
        icon: 'ship',
        closable: true,
      });
      return;
    }

    // ── FM routes ──
    const fmsPageTitles: Record<string, string> = {
      rates: 'FMS Rates',
      'ocean-import': 'Ocean Import',
      'ocean-export': 'Ocean Export',
      air: 'Air Import / Export',
      consol: 'FMS Consol',
      documents: 'FMS Documents',
      accounting: 'FMS Accounting',
      shipments: 'FMS Shipments',
      containers: 'FMS Containers',
      cargo: 'FMS Cargo',
      costing: 'FMS Costing',
      billing: 'FMS Billing',
      transhipment: 'FMS Transhipment',
      'cross-trade': 'FMS Cross Trade',
      'local-handling': 'FMS Local Handling',
      schedule: 'FMS Schedule',
      'port-system': 'FMS Port System',
      setup: 'FMS Setup',
      'reports/shipments': 'FMS Shipment Report',
      'reports/billing': 'FMS Billing Report',
    };
    if (pathname === '/fm') {
      openTab({ title: 'FM Overview', path, icon: 'ship', closable: true });
      return;
    }
    const fmsShipmentDetail = matchPath('/fm/shipments/:shipmentId', pathname);
    if (fmsShipmentDetail?.params.shipmentId) {
      openTab({ title: 'Shipment', path, icon: 'ship', closable: true });
      return;
    }
    const fmsReportsMatch = matchPath('/fm/reports/:page', pathname);
    if (fmsReportsMatch?.params.page) {
      const page = fmsReportsMatch.params.page;
      const key = `reports/${page}`;
      openTab({ title: fmsPageTitles[key] ?? `FMS ${key}`, path, icon: 'ship', closable: true });
      return;
    }
    const fmsMatch = matchPath('/fm/:page', pathname);
    if (fmsMatch?.params.page) {
      const page = fmsMatch.params.page;
      openTab({ title: fmsPageTitles[page] ?? `FMS ${page}`, path, icon: 'ship', closable: true });
      return;
    }

    // ── Maritime Desk routes ──
    const linerPageTitles: Record<string, string> = {
      'master-data': 'Maritime Desk Master Data',
      accounts: 'Maritime Desk Agency Accounts',
      commercial: 'Maritime Desk Commercial',
      billing: 'Maritime Desk Billing',
      'port-calls': 'Maritime Desk Port Calls',
      bookings: 'Maritime Desk Agency Jobs',
      'port-call-docs': 'Maritime Desk Port Call Docs',
      documentation: 'Maritime Desk Port Call Docs',
      intake: 'Maritime Desk AI Intake',
      release: 'Maritime Desk Release',
      'local-services': 'Maritime Desk Local Services',
      'tally-control': 'Maritime Desk Tally Control',
      schedules: 'Maritime Desk Schedules',
      'principal-tasks': 'Maritime Desk Principal Tasks',
      settings: 'Maritime Desk Settings',
    };
    if (pathname === '/liner' || pathname === '/shipping-agency' || pathname === '/maritime-desk') {
      openTab({ title: 'Maritime Desk Overview', path, icon: 'anchor', closable: true });
      return;
    }
    const linerMatch = matchPath('/liner/:page', pathname)
      ?? matchPath('/shipping-agency/:page', pathname)
      ?? matchPath('/maritime-desk/:page', pathname);
    if (linerMatch?.params.page) {
      const page = linerMatch.params.page;
      openTab({ title: linerPageTitles[page] ?? `Maritime Desk ${page}`, path, icon: 'anchor', closable: true });
      return;
    }

    // ── Depot routes ──
    const depotPageTitles: Record<string, string> = {
      accounts: 'Depot Accounts',
      profitability: 'Depot Profitability',
      revenue: 'Depot Revenue',
      tariff: 'Depot Tariff',
      gate: 'Depot Gate',
      yard: 'Depot Yard',
      containers: 'Depot Containers',
      inspections: 'Depot Inspections',
      moves: 'Depot Moves',
      maintenance: 'Depot Maintenance',
      billing: 'Depot Billing',
    };
    if (pathname === '/depot') {
      openTab({ title: 'Depot Overview', path, icon: 'container', closable: true });
      return;
    }
    const depotMatch = matchPath('/depot/:page', pathname);
    if (depotMatch?.params.page) {
      const page = depotMatch.params.page;
      openTab({ title: depotPageTitles[page] ?? `Depot ${page}`, path, icon: 'container', closable: true });
      return;
    }

    // ── SOC routes ──
    const socPageTitles: Record<string, string> = {
      commercial: 'SOC Commercial',
      contracts: 'SOC Contracts',
      revenue: 'SOC Revenue',
      pipeline: 'SOC Pipeline',
      equipment: 'SOC Equipment',
      releases: 'SOC Releases',
      exceptions: 'SOC Exceptions',
      repairs: 'SOC Repairs',
      billing: 'SOC Billing',
    };
    if (pathname === '/soc') {
      openTab({ title: 'SOC Overview', path, icon: 'container', closable: true });
      return;
    }
    const socMatch = matchPath('/soc/:page', pathname);
    if (socMatch?.params.page) {
      const page = socMatch.params.page;
      openTab({ title: socPageTitles[page] ?? `SOC ${page}`, path, icon: 'container', closable: true });
      return;
    }

    // ── HMS routes ──
    const hmsPageTitles: Record<string, string> = {
      jobs: t('nav.Job Orders', 'Haulage Jobs'),
      planner: t('nav.Planner', 'Planner'),
      dispatch: t('nav.Dispatch', 'Dispatch'),
      billing: t('nav.Billing', 'Billing'),
      containers: t('nav.Containers', 'Containers'),
      trips: t('nav.Trips', 'Trips'),
      costing: t('nav.Costing', 'Costing'),
      rates: t('nav.Rates', 'Rates'),
      'event-log': t('nav.Event Log', 'Event Log'),
      'master-data': t('nav.Master Data', 'Master Data'),
      'port-system': t('nav.Port System', 'Port System'),
      documents: t('nav.Documents', 'Documents'),
      'matchbox-reuse': t('hmsMatchbox.title', 'MatchBox Re-use Leads'),
      'driver-log': t('nav.Driver Log', 'Driver Log'),
      pricing: t('nav.Pricing', 'Pricing'),
      setup: t('nav.Setup', 'Setup'),
      'reports/customer': t('nav.By Customer', 'Customer Report'),
      'reports/date': t('nav.By Date', 'Date Report'),
      'reports/driver-pay': t('nav.Driver Pay', 'Driver Pay Report'),
      'compliance/tr128': t('nav.TR128 Audit', 'TR128 Audit'),
      'compliance/co2e': t('nav.CO2e Report', 'CO2e Report'),
    };
    if (pathname === '/hms') {
      openTab({ title: 'HMS Overview', path, icon: 'container-truck', closable: true });
      return;
    }
    const hmsJobDetail = matchPath('/hms/jobs/:jobId', pathname);
    if (hmsJobDetail?.params.jobId) {
      openTab({ title: t('nav.Job Detail', 'Haulage Job Detail'), path, icon: 'container-truck', closable: true });
      return;
    }
    const hmsComplianceMatch = matchPath('/hms/compliance/:page', pathname);
    if (hmsComplianceMatch?.params.page) {
      const page = hmsComplianceMatch.params.page;
      const key = `compliance/${page}`;
      openTab({ title: hmsPageTitles[key] ?? `${t('category.haulage', 'Haulage Management V1')} ${key}`, path, icon: 'shield-check', closable: true });
      return;
    }
    const hmsReportMatch = matchPath('/hms/reports/:page', pathname);
    if (hmsReportMatch?.params.page) {
      const page = hmsReportMatch.params.page;
      const key = `reports/${page}`;
      openTab({ title: hmsPageTitles[key] ?? t('nav.Reports', 'Reports'), path, icon: 'container-truck', closable: true });
      return;
    }
    const hmsMatch = matchPath('/hms/:page', pathname);
    if (hmsMatch?.params.page) {
      const page = hmsMatch.params.page;
      openTab({ title: hmsPageTitles[page] ?? `${t('category.haulage', 'Haulage Management V1')} ${page}`, path, icon: 'container-truck', closable: true });
      return;
    }

    // ── FLM routes ──
    const flmPageTitles: Record<string, string> = {
      assets: 'Fleet Assets',
      'work-orders': 'Work Orders',
      workshop: 'Work Orders',
      parts: 'Parts',
      tyres: 'Tyres',
      costing: 'Costing',
      reports: 'Reports',
    };
    if (pathname === '/flm') {
      openTab({ title: 'FLM Overview', path, icon: 'truck', closable: true });
      return;
    }
    const flmMatch = matchPath('/flm/:page', pathname);
    if (flmMatch?.params.page) {
      const page = flmMatch.params.page;
      openTab({ title: flmPageTitles[page] ?? `FLM ${page}`, path, icon: 'truck', closable: true });
      return;
    }

    // ── OMS routes ──
    const omsPageTitles: Record<string, string> = {
      pipeline: 'Business Pipeline',
      schedule: 'Daily Schedule',
    };
    const omsMatch = matchPath('/oms/:page', pathname);
    if (omsMatch?.params.page) {
      const page = omsMatch.params.page;
      openTab({ title: omsPageTitles[page] ?? `OMS ${page}`, path, icon: 'briefcase', closable: true });
      return;
    }

    // ── Audit routes ──
    const auditPageTitles: Record<string, string> = {
      disputes: 'Disputes',
      'bill-audit': 'Bill Audit',
      settlement: 'Settlement',
    };
    if (pathname === '/audit') {
      openTab({ title: 'Audit & Settlement', path, icon: 'shield-check', closable: true });
      return;
    }
    const auditMatch = matchPath('/audit/:page', pathname);
    if (auditMatch?.params.page) {
      const page = auditMatch.params.page;
      openTab({ title: auditPageTitles[page] ?? `Audit ${page}`, path, icon: 'shield-check', closable: true });
      return;
    }

    // ── SCM routes ──
    const scmPageTitles: Record<string, string> = {
      'sales-orders': 'Sales Orders', quotations: 'Quotations', customers: 'Customers',
      'purchase-orders': 'Purchase Orders', suppliers: 'Suppliers', rfq: 'RFQ',
      products: 'Products', inventory: 'Inventory', 'stock-movements': 'Stock Movements',
      fulfillment: 'Fulfillment', 'shipment-tracking': 'Shipment Tracking',
      costing: 'Costing', reports: 'Reports',
    };
    if (pathname === '/scm') {
      openTab({ title: 'SCM Overview', path, icon: 'layers', closable: true });
      return;
    }
    const scmMatch = matchPath('/scm/:page', pathname);
    if (scmMatch?.params.page) {
      const page = scmMatch.params.page;
      openTab({ title: scmPageTitles[page] ?? `SCM ${page}`, path, icon: 'layers', closable: true });
      return;
    }

    // ── SVC routes ──
    const svcPageTitles: Record<string, string> = {
      orders: 'Service Orders', dispatch: 'Dispatch',
      equipment: 'Equipment', crew: 'Crew', sites: 'Sites',
      billing: 'Billing', costing: 'Costing', reports: 'Reports',
      finance: 'Service Finance',
    };
    if (pathname === '/svc') {
      openTab({ title: 'SVC Overview', path, icon: 'wrench', closable: true });
      return;
    }
    const svcMatch = matchPath('/svc/:page', pathname);
    if (svcMatch?.params.page) {
      const page = svcMatch.params.page;
      openTab({ title: svcPageTitles[page] ?? `SVC ${page}`, path, icon: 'wrench', closable: true });
      return;
    }

    // ── Finance routes ──
    const financePageTitles: Record<string, string> = {
      ar: 'Accounts Receivable',
      ap: 'Accounts Payable',
      'ap-ocr': 'AP Invoice OCR',
      'ar-ocr': 'AR Invoice Import (OCR)',
      'payment-ocr': 'Payment Slip OCR',
      receipts: 'Receipts',
      payments: 'Payments',
      journal: 'Journal',
      'ar-collection': 'AR Collection',
      'payment-run': 'Payment Run',
      'exchange-rates': 'Exchange Rates',
      'fixed-assets': 'Fixed Assets',
      'legal-entities': 'Legal Entities',
      'chart-of-accounts': 'Chart of Accounts',
      'accounting-periods': 'Accounting Periods',
      'tax-config': 'Tax Config',
      'charge-codes': 'Charge Codes',
      netting: 'AR/AP Netting',
      'doc-center': 'Finance Document Center',
      costing: 'Costing',
      billing: 'Billing Workspace',
      accounting: 'Accounting',
      'e-invoicing-lab': 'E-Invoicing Lab',
      'bank-accounts': 'Bank Accounts',
      'bank-statements': 'Bank Statements',
      reconciliation: 'Reconciliation',
      disputes: 'Disputes',
      'bill-audit': 'Bill Audit',
      'reports/ar-aging': 'AR Aging',
      'reports/ap-aging': 'AP Aging',
      'reports/soa': 'Statement of Account',
      'reports/gl': 'General Ledger',
      'reports/trial-balance': 'Trial Balance',
      'reports/pl': 'Profit & Loss',
      'reports/balance-sheet': 'Balance Sheet',
      'reports/cash-flow': 'Cash Flow',
      'reports/tax': 'Tax Report',
      'reports/management': 'Management Accounts',
      'reports/audit-report': 'Audit Report',
      'reports/process-audit': 'Process Audit',
      'reports/consolidation': 'Consolidation',
      'reports/pl-compare': 'P&L Compare',
      'reports/wip-revenue': 'WIP Revenue',
      setup: 'Financial Setup',
    };
    if (pathname === '/finance') {
      openTab({ title: 'Finance Overview', path, icon: 'calculator', closable: true });
      return;
    }
    const financeReportsMatch = matchPath('/finance/reports/:page', pathname);
    if (financeReportsMatch?.params.page) {
      const page = financeReportsMatch.params.page;
      const key = `reports/${page}`;
      openTab({ title: financePageTitles[key] ?? `Finance ${key}`, path, icon: 'calculator', closable: true });
      return;
    }
    const financeMatch = matchPath('/finance/:page', pathname);
    if (financeMatch?.params.page) {
      const page = financeMatch.params.page;
      openTab({ title: financePageTitles[page] ?? `Finance ${page}`, path, icon: 'calculator', closable: true });
      return;
    }

    // ── Cross-module Reports Hub ──
    if (pathname === '/reports') {
      openTab({ title: 'Reports', path, icon: 'bar-chart-3', closable: true });
      return;
    }

    // ── Operational Intelligence routes ──
    const isZh = i18n.resolvedLanguage?.startsWith('zh') ?? false;
    const oiPageTitles: Record<string, string> = {
      'quick-capture': isZh ? '快速采集' : 'Quick Capture',
      'management-brief': isZh ? '管理简报' : 'Management Brief',
      incidents: isZh ? '事件智能' : 'Incident Intelligence',
      'service-centre-ops': isZh ? '服务中心运营' : 'Service Centre Ops',
      'inventory-anomaly': isZh ? '库存异常' : 'Inventory Anomaly',
      'warranty-complaints': isZh ? '保修与投诉' : 'Warranty & Complaints',
      ehs: isZh ? '环境、健康与安全' : 'Environment, Health & Safety',
      'fleet-health': isZh ? '车队健康' : 'Fleet Health',
      'data-records': 'Operational Registers',
      'data-records/documents': isZh ? '文档与证据' : 'Documents & Evidence',
      documents: isZh ? '文档与证据' : 'Documents & Evidence',
    };
    if (pathname === '/operational-intelligence') {
      openTab({ title: isZh ? '运营智能' : 'Operational Intelligence', path, icon: 'radar', closable: true });
      return;
    }
    const oiNestedMatch = matchPath('/operational-intelligence/:page/:subpage', pathname);
    if (oiNestedMatch?.params.page && oiNestedMatch.params.subpage) {
      const key = `${oiNestedMatch.params.page}/${oiNestedMatch.params.subpage}`;
      openTab({ title: oiPageTitles[key] ?? `${isZh ? '运营智能' : 'Operational Intelligence'} ${key}`, path, icon: 'radar', closable: true });
      return;
    }
    const oiMatch = matchPath('/operational-intelligence/:page', pathname);
    if (oiMatch?.params.page) {
      const page = oiMatch.params.page;
      openTab({ title: oiPageTitles[page] ?? `${isZh ? '运营智能' : 'Operational Intelligence'} ${page}`, path, icon: 'radar', closable: true });
      return;
    }

    if (pathname === '/executive-intelligence') {
      openTab({ title: 'Executive Intelligence', path, icon: 'crown', closable: true });
      return;
    }

    // ── Settings routes ──
    const settingsPageTitles: Record<string, string> = {
      company: 'Company Settings',
      storefront: 'Storefront Lite',
      'surface-access': 'Surface Access',
      'running-numbers': 'Running Numbers',
      'recycle-bin': 'Recycle Bin',
      'activity-log': 'Activity Log',
      'event-log': 'Event Log',
      general: 'General Settings',
      profile: 'My Profile',
      team: 'Team Members',
      templates: 'Document Templates',
      billing: 'Billing',
    };
    if (pathname === '/settings') {
      openTab({ title: 'Company Settings', path: '/settings/company', icon: 'building-2', closable: true });
      navigate('/settings/company', { replace: true });
      return;
    }
    const settingsMatch = matchPath('/settings/:page', pathname);
    if (settingsMatch?.params.page) {
      const page = settingsMatch.params.page;
      openTab({ title: settingsPageTitles[page] ?? 'Settings', path, icon: 'settings', closable: true });
      return;
    }

    // ── PRO2 routes ──
    const pro2PageTitles: Record<string, string> = {
      'import/shipments': 'PRO2 Import Shipments',
      'import/containers': 'PRO2 Import Containers',
      'import/cargo': 'PRO2 Import Cargo',
      'export/shipments': 'PRO2 Export Shipments',
      'export/bookings': 'PRO2 Export Bookings',
      'export/containers': 'PRO2 Export Containers',
      'export/cargo': 'PRO2 Export Cargo',
      'finance/documents': 'PRO2 Invoice / Cost Note',
      'finance/charges': 'PRO2 Charge Lines',
      'finance/fx': 'PRO2 FX',
      'master/companies': 'PRO2 Companies',
    };
    if (pathname === '/pro2') {
      openTab({ title: 'PRO2', path, icon: 'database', closable: true });
      return;
    }
    const pro2Match = matchPath('/pro2/:section/:page', pathname);
    if (pro2Match?.params.section && pro2Match?.params.page) {
      const page = `${pro2Match.params.section}/${pro2Match.params.page}`;
      openTab({ title: pro2PageTitles[page] ?? `PRO2 ${page}`, path, icon: 'database', closable: true });
      return;
    }

    // ── Custom App routes ──
    const appMatch = matchPath('/app/:slug', pathname);
    if (appMatch?.params.slug) {
      const slug = appMatch.params.slug;
      const label = slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      openTab({ title: label, path, icon: 'wand-2', closable: true });
      return;
    }
  }, [chatOnlyAccess, config.navigation, ensureTab, hasHomeTab, location.pathname, location.search, navigate, openTab, sessionScope, strictNavigationDefaultIcon, strictNavigationDefaultPath, strictNavigationDefaultTitle, strictNavigationEnabled, t]);

  useEffect(() => {
    if (!strictNavigationEnabled || config.navigation.length === 0) return;
    for (const tab of tabs) {
      const item = navigationItemForPath(pathnameForPath(tab.path), config.navigation);
      const title = labelForNavigationItem(t, item);
      if (title && tab.title !== title) {
        updateTabTitle(tab.id, title);
      }
    }
  }, [config.navigation, strictNavigationEnabled, tabs, t, updateTabTitle]);

  useEffect(() => {
    if (!strictNavigationEnabled || config.navigation.length === 0 || tabs.length === 0) return;
    const hasOutOfScopeTabs = tabs.some((tab) => !isPathAllowedByStrictNavigation(pathnameForPath(tab.path), config.navigation));
    if (!hasOutOfScopeTabs) return;
    const nextPath = closeTabsToDashboard({
      dashboardPath: strictNavigationDefaultPath,
      dashboardTitle: strictNavigationDefaultTitle,
      dashboardIcon: strictNavigationDefaultIcon,
      scope: (tab) => !isPathAllowedByStrictNavigation(pathnameForPath(tab.path), config.navigation),
    });
    if (!isPathAllowedByStrictNavigation(location.pathname, config.navigation) && location.pathname !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [closeTabsToDashboard, config.navigation, location.pathname, navigate, strictNavigationDefaultIcon, strictNavigationDefaultPath, strictNavigationDefaultTitle, strictNavigationEnabled, tabs]);

  // Keep the visible keep-alive tab aligned with the actual browser URL.
  // This guards against session restore or stale tab state leaving the old
  // dashboard tab active after the route has already changed.
  useEffect(() => {
    const routePath = `${location.pathname}${location.search || ''}`;
    const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? null;
    const activePath = activeTab?.path ?? null;

    if (activePath === routePath) return;

    const matchingTab = tabs.find((tab) => tab.path === routePath);
    if (matchingTab && matchingTab.id !== activeTabId) {
      activateTab(matchingTab.id);
    }
  }, [activateTab, activeTabId, location.pathname, location.search, tabs]);

  // ── Navigation handler (from sidebar, command palette, etc.) ──
  const handleNavigate = useCallback(
    (path: string, title: string, icon?: string) => {
      openTab({ title, path, icon, closable: path !== '/' });
      navigate(path);
      setCmdOpen(false);
    },
    [navigate, openTab],
  );

  // ── MDI tab click ──
  const handleTabSelect = useCallback(
    (tabId: string) => {
      activateTab(tabId);
      const tab = tabs.find((t) => t.id === tabId);
      if (tab) navigate(tab.path);
    },
    [activateTab, tabs, navigate],
  );

  // ── MDI tab close ──
  const handleTabClose = useCallback(
    (tabId: string) => {
      const nextPath = closeTab(tabId);
      if (nextPath) navigate(nextPath);
      else navigate(isWasco ? '/wp' : '/');
    },
    [closeTab, isWasco, navigate],
  );

  // ── MDI close all tabs ──
  const handleCloseAllTabs = useCallback(() => {
    if (currentDynamicRuntimeBinding) {
      const nextPath = closeTabsToDashboard({
        dashboardPath: currentDynamicRuntimeBinding.defaultRoute,
        dashboardTitle: titleForResolvedRuntimePath(
          currentDynamicRuntimeBinding.defaultRoute,
          currentDynamicRuntimeBinding,
        ) ?? currentDynamicRuntimeBinding.categoryLabel ?? 'Dashboard',
        dashboardIcon: currentDynamicRuntimeBinding.categoryIcon ?? 'layout-dashboard',
        scope: (tab) => isPathInResolvedDynamicRuntimeBinding(tab.path, currentDynamicRuntimeBinding),
      });
      navigate(nextPath);
      return;
    }
    const nextPath = closeAllTabs();
    navigate(nextPath ?? '/');
  }, [closeAllTabs, closeTabsToDashboard, currentDynamicRuntimeBinding, navigate]);

  // Standalone mode: no sidebar, no header — just content
  if (!showNav) {
    return (
      <div className="h-screen overflow-y-auto bg-background p-4">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* ── Sidebar (two-column: icon rail + submenu) ── */}
      <aside className="flex shrink-0 text-sidebar-foreground transition-all duration-200">
        <Sidebar collapsed={sidebarCollapsed} onNavigate={handleNavigate} />
      </aside>

      {/* ── Main Content Area ── */}
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        {/* ── TopBar — components controlled by DSL feature flags ── */}
        <header className="flex h-12 shrink-0 items-center gap-2 border-b bg-background px-4 py-0">
          {/* Left: Tenant name / workspace context */}
          <div className="flex min-w-0 flex-[2_1_0%] items-center">
            {chatOnlyAccess ? (
              <span className="text-sm font-medium text-foreground">{t('nav.aiChat', 'AI Chat')}</span>
            ) : (tenants && tenants.length > 1) || user?.groupId ? (
              <div className="min-w-0 max-w-[min(56vw,820px)]">
                <TenantSwitchPanel
                  currentTenantId={user?.tenantId}
                  tenantName={tenantName}
                  tenants={tenants}
                  switching={tenantSwitching}
                  onSwitch={handleTenantSwitch}
                />
              </div>
            ) : (
              <span className="flex items-center gap-1.5 text-sm font-medium text-foreground leading-tight max-w-[min(56vw,820px)]" title={tenantName}>
                <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="whitespace-normal break-words">{tenantName}</span>
              </span>
            )}
          </div>

          {/* Center: Search bar trigger (DSL: features.searchEnabled) */}
          {!chatOnlyAccess && features.searchEnabled && features.commandPaletteEnabled && (
            <button
              onClick={() => setCmdOpen(true)}
              className="flex min-w-[160px] max-w-[320px] items-center gap-2 rounded-lg border bg-muted/50 px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted transition-colors w-[min(20vw,320px)]"
            >
              <Search className="h-3.5 w-3.5" />
              <span className="flex-1 text-left">{searchPlaceholder}</span>
              <kbd className="text-[10px] bg-background/80 px-1.5 py-0.5 rounded border">⌘K</kbd>
            </button>
          )}

          {/* Right: tools + user — all DSL-driven visibility */}
          <div className="flex shrink-0 items-center justify-end gap-1">
            {!chatOnlyAccess && features.accentSwitchEnabled && <AccentSwitch />}
            {!chatOnlyAccess && (features.themeSwitchEnabled || features.languageSwitchEnabled) && <ThemeLanguageToggle />}
            {!chatOnlyAccess && features.notificationsEnabled && <NotificationBell />}
            {!chatOnlyAccess && (
              <RealtimeStatusAggregator
                config={config.realtimeStatusAggregator}
                user={user}
                surface="web"
              />
            )}
            {!chatOnlyAccess && <div className="mx-1 h-6 w-px bg-border" />}
            <UserDropdown
              user={user ? { name: sanitizeUserDisplay(user.name ?? user.email), email: sanitizeUserEmail(user.email) ?? undefined } : null}
              onSignOut={logout}
              onProfile={chatOnlyAccess ? undefined : () => {
                openTab({ title: t('settings.profile', 'My Profile'), path: '/settings/profile', icon: 'user', closable: true });
                navigate('/settings/profile');
              }}
              onSettings={chatOnlyAccess ? undefined : () => {
                openTab({ title: t('settings.company', 'Company Settings'), path: '/settings/company', icon: 'building-2', closable: true });
                navigate('/settings/company');
              }}
              onShortcuts={!chatOnlyAccess && features.commandPaletteEnabled ? () => setCmdOpen(true) : undefined}
            />
          </div>
        </header>

        {/* ── MDI Tab Bar (DSL: features.mdiTabsEnabled) ── */}
        {!chatOnlyAccess && features.mdiTabsEnabled && (
          <MDITabs
            tabs={visibleMdiTabs}
            activeTabId={activeTabId}
            onTabSelect={handleTabSelect}
            onTabClose={handleTabClose}
            onCloseAll={handleCloseAllTabs}
          />
        )}

        {/* ── Momentum Signal Bar ── */}
        {!chatOnlyAccess && (
          <MomentumBar
            score={momentum.score}
            signals={momentum.signals}
            topSignals={momentum.topSignals}
            hasCritical={momentum.hasCritical}
            blockedRevenue={momentum.blockedRevenue}
            blockedCurrency={momentum.blockedCurrency}
            enabled={momentum.enabled}
            onToggle={momentum.toggle}
            onAction={handleMomentumAction}
            lang={t('lang_code') === 'zh' ? 'zh' : 'en'}
          />
        )}

        {/* ── Page Content — prefer keep-alive when tab state matches the URL; otherwise render the route outlet directly ── */}
        {shouldRenderKeepAlive && !isDynamicRuntimeWorkspace ? (
          <TabKeepAlive tabs={keepAliveTabs} activeTabId={activeTabId} />
        ) : (
          <div className={cn(
            'flex flex-1 min-h-0 flex-col',
            location.pathname.startsWith('/wp') ? 'overflow-hidden p-0' : 'overflow-y-auto p-4',
          )}>
            <Outlet />
          </div>
        )}
      </div>

      {/* ── Right-docked AI Chat Panel ── */}
      {!chatOnlyAccess && !isWasco && !isNewRevo && <ChatSlidePanel />}

      {/* ── Command Palette Overlay (DSL: features.commandPaletteEnabled) ── */}
      {!chatOnlyAccess && features.commandPaletteEnabled && (
        <CommandPalette
          open={cmdOpen}
          onClose={() => setCmdOpen(false)}
          onNavigate={handleNavigate}
          tenants={tenants}
          currentTenantId={user?.tenantId}
          currentUserId={user?.id}
          onSwitchTenant={handleTenantSwitch}
        />
      )}
    </div>
  );
}
